// The four sound error categories, in one place, so the wording is identical
// wherever a category is offered or shown.
//
// The STORED KEYS never change: omitted / repeated / wrong / other are in every
// row saved since 2024 and in the Transcribe modal's own :value="option".
// Only the labels carry Tamara's wording, because nobody ever sees a stored
// value. This mirrors app/Services/SoundErrorCategories.php in eesa-backend.

export const SOUND_ERROR_CATEGORIES = [
    { key: 'omitted', label: 'Left out', definition: 'The sound was not produced at all.' },
    { key: 'repeated', label: 'Repeated', definition: 'The sound or syllable was said more than once.' },
    { key: 'wrong', label: 'Swapped', definition: 'A different sound was produced in its place.' },
    { key: 'other', label: 'Other', definition: 'Anything else - put it in the note.' },
];

// Kept as a plain list of keys so the radio bindings stay `:value="option"`.
export const SOUND_ERROR_KEYS = SOUND_ERROR_CATEGORIES.map(c => c.key);

const BY_KEY = SOUND_ERROR_CATEGORIES.reduce((all, c) => ({ ...all, [c.key]: c }), {});

export function soundErrorLabel(key) {
    return BY_KEY[key]?.label ?? '';
}

export function soundErrorDefinition(key) {
    return BY_KEY[key]?.definition ?? '';
}

// Unknown or legacy values are dropped rather than shown, the same way the
// backend's normalize() does: a value nothing can label is not a category.
export function normalizeSoundError(value) {
    if (typeof value !== 'string') return null;

    const key = value.trim().toLowerCase();

    return SOUND_ERROR_KEYS.includes(key) ? key : null;
}

// The stored column is a positional JSON array parallel to the sound slots, and
// an item that has never been categorised has no field at all - so this never
// throws, whatever the payload holds.
export function parseIncorrectDetails(value) {
    if (Array.isArray(value)) return value.map(normalizeSoundError);

    if (typeof value !== 'string' || !value.trim()) return [];

    try {
        const parsed = JSON.parse(value);

        return Array.isArray(parsed) ? parsed.map(normalizeSoundError) : [];
    } catch {
        return [];
    }
}

// What is shown per sound: the clinician's own choice wins sound by sound, and
// the model's suggestion fills only the sounds they have not answered. Reads
// `error_categories` when the API sends it and falls back to the two raw
// columns, so this works both before and after the backend half is deployed.
export function soundErrorEntries(item) {
    const fromApi = Array.isArray(item?.error_categories) ? item.error_categories : null;

    if (fromApi) {
        return fromApi.map((entry) => {
            if (!entry) return null;

            const key = normalizeSoundError(typeof entry === 'string' ? entry : (entry.key ?? entry.category ?? entry.error));
            if (!key) return null;

            const source = entry?.source === 'ai' ? 'ai' : 'clinician';

            return { key, label: soundErrorLabel(key), source };
        });
    }

    const clinician = parseIncorrectDetails(item?.incorrect_details);
    const suggested = parseIncorrectDetails(item?.ai_incorrect_details);
    const length = Math.max(clinician.length, suggested.length);

    return Array.from({ length }, (_, i) => {
        const key = clinician[i] || suggested[i] || null;
        if (!key) return null;

        return { key, label: soundErrorLabel(key), source: clinician[i] ? 'clinician' : 'ai' };
    });
}

// Where a category came from. A suggestion the clinician never picked or
// confirmed is NOT a label: only what they chose themselves counts as one, so
// the two are never written to the same place.
export const SOURCE_CLINICIAN = 'clinician';
export const SOURCE_AI = 'ai';

// What goes into `incorrect_details`: the clinician's own categories only. A
// suggestion they have not confirmed leaves its slot empty here - the model's
// own suggestion is already stored in `ai_incorrect_details`, which is what
// says it was offered.
export function incorrectDetailsToSave(reasons = [], suggested = []) {
    return (reasons || []).map((key, i) => (key && !suggested?.[i] ? key : null));
}

// The per-sound source of what is on screen, sent alongside: `clinician` for a
// category they picked or confirmed, `ai` for a suggestion still sitting
// unconfirmed, null where there is none. A backend that does not store this
// yet drops it, which costs nothing - the labels above are already honest
// without it.
export function incorrectDetailsSource(reasons = [], suggested = []) {
    return (reasons || []).map((key, i) => {
        if (!key) return null;

        return suggested?.[i] ? SOURCE_AI : SOURCE_CLINICIAN;
    });
}

// Standing wording.
export const SOUND_ERROR_PROMPT = 'Say how each sound marked incorrect went wrong:';

export const SOUND_ERROR_LEGEND =
    SOUND_ERROR_CATEGORIES.map(c => `${c.label} = ${c.definition.replace(/\.$/, '').toLowerCase()}`).join('; ') + '.';

export const SOUND_ERROR_SUGGESTION_NOTE =
    'Suggested from the recording and not saved as your answer yet: change it, or confirm it if it is right.';

export const SOUND_ERROR_SUGGESTED_TAG = 'Suggested, not confirmed';
